<template>
  <div class="relative">
    <v-text-field
      v-if="label.Released"
      variant="solo-filled"
      hide-details
      type="number"
      density="compact"
      :disabled="loadingState === 'loading'"
      class="w-48"
      min="0"
      max="10000"
      v-on:keyup.enter="download"
      :model-value="quantity"
      @update:model-value="
        (val) => {
          loadingState = 'unset';
          quantity = checkInput(val);
        }
      "
    >
      <template #append-inner="{ blur }">
        <v-btn
          @vue:mounted="blurFunction = blur"
          :disabled="quantity === 0"
          @click="download()"
          density="comfortable"
          :color="quantity === 0 ? '' : 'primary'"
          :variant="quantity === 0 ? 'tonal' : 'elevated'"
        >
          <v-fade-transition leave-absolute>
            <v-progress-circular
              v-if="showProgress"
              :size="22"
              :width="2"
              :model-value="shownDownloadProgress * 100"
            ></v-progress-circular>
            <v-icon
              v-else
              :icon="loadingState === 'success' ? 'mdi-check' : 'mdi-download'"
            ></v-icon>
          </v-fade-transition>
        </v-btn>
      </template>
    </v-text-field>
    <v-tooltip v-else>
      <template #activator="{ props }">
        <v-icon v-bind="props">mdi-information</v-icon>
      </template>
      <template #default>{{
        i18n.global.t("app.printLabels.info.notReleasedInfo")
      }}</template>
    </v-tooltip>
    <v-snackbar v-model="showDownloadInfo" :timeout="5000">
      {{
        i18n.global.t("app.printLabels.loadingDownload.success", {
          label: label.Layout,
        })
      }}
      <template v-slot:actions>
        <v-btn color="primary" variant="text" @click="showDownloadInfo = false">
          {{ i18n.global.t('global.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script setup lang="ts">
import { PropType, ref, watch } from "vue";

import { PrintItems } from "@/api-client/responses";
import { useApplicationStore, useLabelStore } from "@/store";
import { i18n } from "@/locales";

// get instances
const labelStore = useLabelStore();
const applicationStore = useApplicationStore();

const quantity = ref(0);
const loadingState = ref<loadingStateType>("unset");
const blurFunction = ref<() => void | undefined>();

const showDownloadInfo = ref(false);

const props = defineProps({
  label: {
    required: true,
    type: Object as PropType<PrintItems.PrintItem>,
  },
});

// Only input numbers
function checkInput(value: string): number {
  let checkedValue = 0;
  // parse value
  checkedValue = Number(value);
  // check if it is a number
  if (isNaN(checkedValue)) checkedValue = 0;
  // limit to 10000
  if (Number(value) > 10000) checkedValue = 10000;
  // limit to 0
  if (Number(value) < 0) checkedValue = 0;
  //remove decimals
  if (Number(value) % 1 !== 0)
    checkedValue = Number(String(value).split(".")[0]);
  // return value
  return checkedValue;
}

async function download() {
  if (quantity.value <= 0) return;

  loadingState.value = "loading";
  try {
    await labelStore.downloadLabel({
      label: props.label,
      quantity: quantity.value,
    });
  } catch (error: unknown) {
    applicationStore.addAlert({
      key: "app.printLabels.loadingDownload.error",
      keyData: {
        label: props.label.Layout,
        quantity: String(quantity.value),
      },
      type: "Warning",
    });
    loadingState.value = "failed";
    blurFunction.value ? blurFunction.value() : "";
    return;
  }
  quantity.value = 0;
  blurFunction.value ? blurFunction.value() : "";
  loadingState.value = "success";
  showDownloadInfo.value = true;
}

//#region loading bar
const maxApiResponseSeconds = 10;
const intervalId = ref<null | number>(null);

const fadeOutTimerId = ref<null | number>(null);
const showProgress = ref(false);

const downloadProgress = ref(0);
const shownDownloadProgress = ref(0);

watch(loadingState, async (state) => {
  // on start loading
  if (state === "loading") {
    // show progress bar
    showProgress.value = true;
    if (fadeOutTimerId.value) {
      clearTimeout(fadeOutTimerId.value);
      fadeOutTimerId.value = null;
    }
    shownDownloadProgress.value = 0;

    // make the progress jump from 0% to 5% to look more responsive
    (async () => {
      // wait 1ms, otherwise the transition from 0 to 5 won't appear
      await new Promise((res) =>
        setTimeout(() => {
          res(0);
        }, 1)
      );
      // start shown progress
      shownDownloadProgress.value = 0.05;
    })();

    // reset progress
    downloadProgress.value = 0;
    // start interval
    intervalId.value = setInterval(() => {
      downloadProgress.value += 0.1;
      // calculate shown progress
      shownDownloadProgress.value = downloadProgress.value * 0.95 + 0.05;
      // stop timer when reaches 100
      if (downloadProgress.value >= 1 && intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
      }
    }, maxApiResponseSeconds * 100);
    return;
  }

  if (state === "success") {
    shownDownloadProgress.value = 1;
    downloadProgress.value = 1;
  }
  if (state === "failed") {
    shownDownloadProgress.value = 0;
    downloadProgress.value = 0;
  }

  // hide progress bar after 2 seconds
  if (fadeOutTimerId.value) {
    clearTimeout(fadeOutTimerId.value);
    fadeOutTimerId.value = null;
  }
  fadeOutTimerId.value = setTimeout(() => {
    showProgress.value = false;
  }, 300);
});

//#endregion
</script>
<style lang="scss" scoped></style>
